import React, { useState } from 'react';
import { useFlexibleList } from '../../../../hooks/useSWR';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';

const DeviationAuditLogTab = ({ selectedRecord }) => {
  const { theme } = useTheme();
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  
  // Fetch deviation audit logs using master entity endpoint (includes all related entities)
  // Entity type ID 8 is for Deviation
  const { data: auditLogs = [], isLoading, error } = useFlexibleList(
    selectedRecord?.id ? `/audit-log/master/8/${selectedRecord.id}` : null
  );

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      // Use dd/MM/yyyy HH:mm:ss format for audit logs (with seconds for precision)
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getActionIcon = (action, entityType) => {
    switch (action.toUpperCase()) {
      case 'CREATE':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        );
      case 'UPDATE':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      case 'DELETE':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const toggleExpand = (logId) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return (
      <div className={cn("p-4 text-center", theme.text.muted)}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-sm">Loading audit logs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("p-4 text-center text-sm", theme.text.error)}>
        Error loading audit logs: {error.message || 'Unknown error'}
      </div>
    );
  }

  if (auditLogs.length === 0) {
    return (
      <div className={cn("p-4 text-center text-sm", theme.text.muted)}>
        No audit logs found
      </div>
    );
  }

  return (
    <div 
      className="p-4 flex-1 overflow-y-auto advanced-table-scroll"
      style={{ 
        scrollbarWidth: 'thin',
        minHeight: 0,
        maxHeight: '100%'
      }}
    >
      <div className="space-y-3">
        {auditLogs.map((log) => {
          const isExpanded = expandedLogs.has(log.id);
          const hasDetails = log.oldValues || log.newValues || (log.changedFields && log.changedFields.length > 0);
          
          return (
            <div
              key={log.id}
              className={cn(
                "rounded-lg border p-3 transition-colors",
                theme.border.subtle,
                theme.colors.background,
                "hover:border-opacity-60"
              )}
            >
              <div className="flex items-start gap-3">
                <div className={cn(
                  "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                  theme.colors.primary,
                  theme.text.inverse
                )}>
                  {getActionIcon(log.action, log.entityType?.name)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div>
                      <div className={cn("text-sm font-medium", theme.text.primary)}>
                        {log.action} - {log.entityType?.name || 'Unknown Entity'}
                      </div>
                      {log.description && (
                        <div className={cn("text-xs mt-1", theme.text.secondary)}>
                          {log.description}
                        </div>
                      )}
                    </div>
                    {hasDetails && (
                      <button
                        onClick={() => toggleExpand(log.id)}
                        className={cn(
                          "text-xs px-2 py-1 rounded transition-colors",
                          theme.colors.primary,
                          theme.text.inverse,
                          "hover:opacity-80"
                        )}
                      >
                        {isExpanded ? 'Hide' : 'Show'} Details
                      </button>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3 text-xs mt-2">
                    <div className={cn("flex items-center gap-1", theme.text.muted)}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span>{log.user?.userDisplayName || log.user?.name || 'System'}</span>
                    </div>
                    <div className={cn("flex items-center gap-1", theme.text.muted)}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{formatDateTime(log.performedAt)}</span>
                    </div>
                    {log.ipAddress && (
                      <div className={cn("flex items-center gap-1", theme.text.muted)}>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                        <span>{log.ipAddress}</span>
                      </div>
                    )}
                  </div>
                  
                  {isExpanded && hasDetails && (
                    <div className={cn("mt-3 pt-3 border-t space-y-2", theme.border.subtle)}>
                      {log.changedFields && log.changedFields.length > 0 && (
                        <div>
                          <div className={cn("text-xs font-medium mb-1", theme.text.secondary)}>
                            Changed Fields:
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {log.changedFields.map((field, idx) => (
                              <span
                                key={idx}
                                className={cn(
                                  "inline-flex items-center px-2 py-0.5 rounded text-xs",
                                  theme.colors.primary,
                                  theme.text.inverse
                                )}
                              >
                                {field}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {log.oldValues && (
                        <div>
                          <div className={cn("text-xs font-medium mb-1", theme.text.secondary)}>
                            Old Values:
                          </div>
                          <pre className={cn(
                            "text-xs p-2 rounded overflow-x-auto",
                            theme.colors.backgroundAlt,
                            theme.text.primary
                          )}>
                            {JSON.stringify(log.oldValues, null, 2)}
                          </pre>
                        </div>
                      )}
                      
                      {log.newValues && (
                        <div>
                          <div className={cn("text-xs font-medium mb-1", theme.text.secondary)}>
                            New Values:
                          </div>
                          <pre className={cn(
                            "text-xs p-2 rounded overflow-x-auto",
                            theme.colors.backgroundAlt,
                            theme.text.primary
                          )}>
                            {JSON.stringify(log.newValues, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DeviationAuditLogTab;

